<template>
    <cr-loading v-if="!challenge" :message="t('CHALLENGE_FORM_LOADING_MESSAGE')"/>
    <template v-else>
        <image-artifact v-if="imageArtifact" :artifact="imageArtifact" />
        <bot-artifact v-if="botArtifact" :challenge="challenge" :reset-key="botResetKey" />
        <cr-form @confirm="onConfirm">
            <component  :is="challengeComponent" :key="resetKey" @update:model-value="onAnswerUpdated" :challenge/>
            <div class="challenge-form-controls cr-mt6">
                <cr-button class="challenge-form-submit-button cr-mr3" type="submit" :disabled="isLoading" background-color="cr-primary-button-color" text-color="cr-primary-button-text-color" data-testing="challenge-form-confirm">
                    {{ t('CHALLENGE_LAYOUT_FORM_BUTTONS_SUBMIT') }}
                </cr-button>
                <cr-button text :disabled="isLoading" :to="Route.LevelSelect" data-testing="challenge-form-cancel">
                    {{ t('CHALLENGE_LAYOUT_F0RM_BUTTONS_BACK') }}
                </cr-button>
            </div>
        </cr-form>
    </template>
</template>
<style scoped>
.challenge-form-controls .cr-button
{
    border-radius: var(--cr-pill-button-border-radius);

}
.challenge-form-controls
{
    display: flex;
    justify-content: center;
}
.cr-loading {
    align-self: center;
}
</style>

<script setup lang="ts">
import TextareaChallenge from './TextareaChallenge.vue';
import TextChallenge from './TextChallenge.vue';
import MultipleChoiceChallenge from './MultipleChoiceChallenge.vue';
import MultipleSelectChallenge from './MultipleSelectChallenge.vue';
import BotArtifact from './BotArtifact.vue';
import ImageArtifact from './ImageArtifact.vue';
import { ChallengeArtifactType, IChallenge } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useApiClientStore } from '../../stores/apiClientStore';
import Route from '../../routers/route';

let { isLoading } = storeToRefs(useApiClientStore());

const props = defineProps<{
    challenge?: IChallenge; 
    disabled?: boolean;
    resetKey?: number;
    botResetKey?: number;
}>();

const emit = defineEmits<{
    (name: 'submit', challengeId: string, answer: string|string[]): void,
}>();

const { t } = useI18n();

const botArtifact = computed(() => props.challenge?.artifacts?.find(artifact => artifact.type === ChallengeArtifactType.Bot));
const imageArtifact = computed(() => props.challenge?.artifacts?.find(artifact => artifact.type === ChallengeArtifactType.File && artifact.display));

const challengeComponent = computed(() => 
{
    if (props.challenge && 'choice' in props.challenge && props.challenge.choice)
    {
        return props.challenge.choice.required === 1 ? MultipleChoiceChallenge : MultipleSelectChallenge
    }
    else if (!!botArtifact.value) // assume that bot artifacts have string or regex flags for now
    {
        return TextChallenge;
    }
    else
    {
        return TextareaChallenge;
    }
})

const currentAnswer = ref<string|string[]>("");

watch(() => props.resetKey, () =>
{
    currentAnswer.value = "";
});

const onAnswerUpdated = (value: string|string[]) =>
{
    currentAnswer.value = value;
}

const onConfirm = async () =>
{
    // challenge must be present for the form to be present
    emit('submit', props.challenge!.id, currentAnswer.value);
}
</script>
